import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  updateProfile as fbUpdateProfile,
  type User,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { auth, db, firebaseReady } from '../lib/firebase';
import { ADMIN_EMAILS } from '../lib/firebase-config';
import type { StaffRole, UserProfile } from '../lib/types';

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  /** 'admin' | 'cleaner' for staff, null for customers/guests */
  role: StaffRole | null;
  isAdmin: boolean;
  isStaff: boolean;
  loading: boolean;
  needsVerification: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, phone: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  refresh: () => Promise<void>;
  saveProfile: (patch: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const isOwnerEmail = (u: User | null) =>
  !!u?.email && ADMIN_EMAILS.includes(u.email.toLowerCase()) && u.emailVerified;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [role, setRole] = useState<StaffRole | null>(null);
  const [loading, setLoading] = useState(firebaseReady);
  const profileUnsub = useRef<(() => void) | null>(null);

  const evaluateRole = async (u: User | null) => {
    if (!u) {
      setRole(null);
      return;
    }
    if (isOwnerEmail(u)) {
      // Force a token refresh so Firestore rules see email_verified: true
      // immediately after verification (the cached token can lag up to 1h).
      await u.getIdToken(true).catch(() => {});
      setRole('admin');
      return;
    }
    try {
      const snap = await getDoc(doc(db(), 'admins', u.uid));
      setRole(snap.exists() ? ((snap.data().role as StaffRole) ?? 'cleaner') : null);
    } catch {
      setRole(null);
    }
  };

  const watchProfile = (u: User | null) => {
    profileUnsub.current?.();
    profileUnsub.current = null;
    if (!u) {
      setProfile(null);
      return;
    }
    profileUnsub.current = onSnapshot(
      doc(db(), 'users', u.uid),
      (snap) => {
        if (snap.exists()) {
          setProfile(snap.data() as UserProfile);
        } else {
          // First sign-in (e.g. via Google) — create the profile doc.
          const fresh: UserProfile = {
            name: u.displayName ?? '',
            email: u.email ?? '',
            phone: '',
            vehicles: [],
            createdAt: Date.now(),
          };
          setDoc(doc(db(), 'users', u.uid), fresh).catch(() => {});
          setProfile(fresh);
        }
      },
      () => setProfile(null),
    );
  };

  useEffect(() => {
    if (!firebaseReady) return;
    const unsub = onAuthStateChanged(auth(), async (u) => {
      setUser(u);
      await evaluateRole(u);
      watchProfile(u);
      setLoading(false);
    });
    return () => {
      unsub();
      profileUnsub.current?.();
    };
  }, []);

  const value: AuthState = {
    user,
    profile,
    role,
    isAdmin: role === 'admin',
    isStaff: role !== null,
    loading,
    needsVerification: !!user && !user.emailVerified && !!user.email && ADMIN_EMAILS.includes(user.email.toLowerCase()),
    signIn: async (email, password) => {
      await signInWithEmailAndPassword(auth(), email, password);
    },
    signUp: async (name, email, phone, password) => {
      const cred = await createUserWithEmailAndPassword(auth(), email, password);
      if (name) await fbUpdateProfile(cred.user, { displayName: name });
      await setDoc(doc(db(), 'users', cred.user.uid), {
        name,
        email,
        phone,
        vehicles: [],
        createdAt: Date.now(),
      } satisfies UserProfile);
      // Owner accounts need a verified email to unlock admin rights.
      if (ADMIN_EMAILS.includes(email.toLowerCase())) await sendEmailVerification(cred.user);
    },
    signInWithGoogle: async () => {
      await signInWithPopup(auth(), new GoogleAuthProvider());
    },
    signOut: async () => {
      await fbSignOut(auth());
    },
    resetPassword: async (email) => {
      await sendPasswordResetEmail(auth(), email);
    },
    resendVerification: async () => {
      if (auth().currentUser) await sendEmailVerification(auth().currentUser!);
    },
    refresh: async () => {
      const u = auth().currentUser;
      if (u) {
        await u.reload();
        await u.getIdToken(true).catch(() => {});
        setUser(auth().currentUser);
        await evaluateRole(auth().currentUser);
      }
    },
    saveProfile: async (patch) => {
      if (!auth().currentUser) return;
      await setDoc(doc(db(), 'users', auth().currentUser!.uid), patch, { merge: true });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
